import React, { useState, useEffect } from "react";
import { Menu, X, User } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="13165" className="hidden md:flex gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-20 bg-muted/20 animate-pulse rounded-sm" data-index={i} />)}
      </div>;
  }
  return <NavigationMenu className="hidden md:flex">
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.label === "Servizi" ? "/servizi" : item.label === "Soluzioni" ? "/soluzioni" : item.label === "Esperienza" ? "/esperienza" : item.label === "Blog" ? "/blog" : item.label === "Contatti" ? "/contatti" : item.label === "Home" ? "/HomePage" : item.href || "#"} className="group inline-flex h-9 w-max items-center justify-center rounded-none bg-transparent px-4 py-2 text-[14px] font-semibold uppercase tracking-widest text-white transition-colors hover:text-primary focus:text-primary focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/10 data-[state=open]:bg-accent/10 relative overflow-hidden">
                {item.label}
                <span className="absolute bottom-0 left-0 h-[1px] w-full translate-y-full bg-primary transition-transform duration-300 group-hover:translate-y-0" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-[#0F172A]/95 backdrop-blur-xl border-b border-white/10 shadow-2xl md:hidden animate-in slide-in-from-top-5 fade-in duration-200">
      <nav className="container mx-auto px-4 py-6 flex flex-col gap-4">
        {loading ? <div className="flex flex-col gap-4">
            {[1, 2, 3].map(i => <div key={i} className="h-8 w-full bg-muted/20 animate-pulse rounded-sm" data-index={i} />)}
          </div> : menuItems.map((item, i) => <Link key={item.id} to={item.label === "Servizi" ? "/servizi" : item.label === "Soluzioni" ? "/soluzioni" : item.label === "Esperienza" ? "/esperienza" : item.label === "Blog" ? "/blog" : item.label === "Contatti" ? "/contatti" : item.label === "Home" ? "/HomePage" : item.href || "#"} className="text-sm font-semibold uppercase tracking-wider text-white hover:text-primary transition-colors py-3 border-b border-white/5 last:border-0" onClick={onClose} data-index={i}>
              {item.label}
            </Link>)}
        <div className="flex flex-col gap-3 mt-4 pt-4 border-t border-white/10">
          <Button variant="ghost" className="justify-start px-0 hover:bg-transparent hover:text-primary text-[14px] font-semibold uppercase tracking-widest" asChild>
            <Link to="#">
              <User className="mr-2 h-4 w-4 text-primary" />
              Area Clienti
            </Link>
          </Button>
          <Button className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 text-[14px] font-bold uppercase tracking-widest" asChild>
            <Link to="/ContactsPage">Richiedi Consulenza</Link>
          </Button>
        </div>
      </nav>
    </div>;
}
function HeaderContent() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header-navigazione" className={cn("sticky top-0 z-50 w-full transition-all duration-300 border-b dark", isScrolled ? "bg-[#0F172A]/70 backdrop-blur-[12px] border-white/10 shadow-lg" : "bg-[#0F172A] border-transparent")}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0">
          <Link to="/HomePage" className="flex items-center gap-2">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570363/img/image_cieydgl1.png" alt="B-Tech IT Consulting Logo" className="h-24 w-auto object-contain" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-center">
          <DesktopMenu />
        </div>

        {}
        <div className="hidden md:flex items-center gap-4">
          <Button variant="ghost" className="text-white hover:text-primary hover:bg-transparent text-[14px] font-semibold uppercase tracking-widest" asChild>
            <Link to="#">Area Clienti</Link>
          </Button>
          <Button className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 px-6 shadow-sm hover:shadow-cyan-500/20 transition-all text-[14px] font-bold uppercase tracking-widest" asChild>
            <Link to="/ContactsPage">Consulenza</Link>
          </Button>
        </div>

        {}
        <div className="md:hidden">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-foreground hover:bg-white/5">
            {isMobileMenuOpen ? <X className="h-6 w-6 text-primary" /> : <Menu className="h-6 w-6 text-primary" />}
            <span className="sr-only">Menu</span>
          </Button>
        </div>
      </div>

      {}
      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
    </header>;
}
export default function Header() {
  return <MenuProvider menu_id="47" className="w-full">
      <HeaderContent />
    </MenuProvider>;
}